import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Loader2, AlertCircle, Inbox, Search, X,
  ChevronRight, Package, Printer,
} from 'lucide-react';
import { useMyOrdini, MyOrdine, MyOrdiniResp } from '../../hooks/useMyOrdini';
import { useAuth } from '../../context/AuthContext';
import { useDebounce } from '../../hooks/useDebounce';
import { statoOrdine } from '../../utils/statoOrdine';
import api from '../../services/api';
import { aggiornaContatori } from '../../hooks/useDashboardCounters';
import { toast } from 'sonner';

/* Un ordine con una novità non vista: stesso rosso del chip del menu. */
function SegnoAggiornato({ id }: { id: string }) {
  return (
    <span data-testid={`order-aggiornato-${id}`}
      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-rose-700">
      <span className="w-1.5 h-1.5 rounded-full bg-rose-600" aria-hidden="true" /> Aggiornato
    </span>
  );
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('it-IT') : '—';

/* ── Row ── */
/* ── Report di stampa: tutti gli ordini dei filtri, non solo la pagina (#6) ── */
function ReportOrdini({ righe, filtri }: { righe: MyOrdine[]; filtri: string }) {
  const { user } = useAuth();
  const totale = righe.reduce((s, o) => s + (o.totaleIvato || 0), 0);
  return (
    <div className="hidden print:block text-[10pt]" data-testid="orders-print-report">
      <h1 className="text-lg font-semibold">Elenco ordini</h1>
      <p className="mb-3">
        {user?.nome}{user?.idCliente ? ` · cod. ${user.idCliente}` : ''} · stampato il {new Date().toLocaleDateString('it-IT')}
        {filtri && ` · ${filtri}`} · {righe.length} ordini
      </p>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b-2 border-black text-left">
            <th className="py-1 pr-2">Data</th><th className="py-1 pr-2">N° ordine</th>
            <th className="py-1 pr-2">Destinazione</th><th className="py-1 pr-2">Stato</th>
            <th className="py-1 text-right">Totale IVA incl.</th>
          </tr>
        </thead>
        <tbody>
          {righe.map(o => (
            <tr key={o.id} className="border-b border-gray-300">
              <td className="py-1 pr-2 whitespace-nowrap">{fmtDate(o.date)}</td>
              <td className="py-1 pr-2 font-mono whitespace-nowrap">{o.id}</td>
              <td className="py-1 pr-2">{o.destination || 'Sede'}</td>
              <td className="py-1 pr-2 whitespace-nowrap">{statoOrdine(o.status).label}</td>
              <td className="py-1 text-right whitespace-nowrap">€ {o.totaleIvato.toFixed(2).replace('.', ',')}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-black font-semibold">
            <td className="py-1" colSpan={4}>Totale</td>
            <td className="py-1 text-right whitespace-nowrap">€ {totale.toFixed(2).replace('.', ',')}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function OrderRow({ ordine, idx }: { ordine: MyOrdine; idx: number }) {
  const navigate = useNavigate();
  const meta = statoOrdine(ordine.status);
  const { key } = ordine;
  const detailPath = `/area-clienti/ordini/${key.tipork}/${key.anno}/${key.serie}/${key.numdoc}`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(detailPath)}
      onKeyDown={e => e.key === 'Enter' && navigate(detailPath)}
      data-testid={`order-row-${ordine.id}`}
      className={`cursor-pointer transition-colors
        ${idx % 2 === 1 ? 'bg-[hsl(210,40%,99%)]' : 'bg-white'}
        hover:bg-[hsl(210,40%,97%)] border-b last:border-b-0
        px-4 py-3 md:py-3.5`}
    >
      {/* ── DESKTOP ──
          La griglia a tracce fisse somma ~548px: sotto md non entra nei
          ~348px disponibili e la card in overflow:hidden tagliava via
          Totale. Da qui in giù si usa il ramo mobile a card, lo stesso
          idioma di DocumentsPage. */}
      <div className="hidden md:grid grid-cols-[7.5rem_9rem_1fr_8rem_7rem_2.5rem] items-center gap-0">
        {/* Data */}
        <div className="text-sm tabular-nums text-muted-foreground">{fmtDate(ordine.date)}</div>

        {/* N° Ordine */}
        <div className="font-mono text-sm font-semibold text-[hsl(210,64%,29%)] whitespace-nowrap">{ordine.id}</div>

        {/* Destinazione */}
        <div className="text-sm truncate pr-2">
          {ordine.destination
            ? <span className="font-medium">{ordine.destination}</span>
            : <span className="text-muted-foreground italic text-xs">—</span>}
        </div>

        {/* Stato */}
        <div className="flex flex-col items-start gap-1">
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${meta.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full flex-none ${meta.dot}`} />
            {meta.label}
          </span>
          {ordine.aggiornato && <SegnoAggiornato id={ordine.id} />}
        </div>

        {/* Totale */}
        <div className="text-right">
          <span className="font-mono font-semibold text-sm tabular-nums">
            € {ordine.totaleIvato.toFixed(2).replace('.', ',')}
          </span>
        </div>

        {/* Arrow */}
        <div className="flex items-center justify-center">
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>

      {/* ── MOBILE ── */}
      <div className="md:hidden space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${meta.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full flex-none ${meta.dot}`} />
            {meta.label}
          </span>
          {ordine.aggiornato && <SegnoAggiornato id={`${ordine.id}-m`} />}
          <span className="text-[13px] tabular-nums text-muted-foreground">
            {fmtDate(ordine.date)}
          </span>
        </div>

        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-[13px] font-semibold text-[hsl(210,64%,29%)]">
            {ordine.id}
          </span>
          <span className="font-mono font-semibold text-[15px] tabular-nums">
            € {ordine.totaleIvato.toFixed(2).replace('.', ',')}
          </span>
        </div>

        {ordine.destination && (
          <p className="text-[13px] text-muted-foreground truncate">{ordine.destination}</p>
        )}

        <div className="flex items-center justify-end pt-0.5">
          {/* La riga intera è già cliccabile (role=button sul contenitore):
              questo è l'affordance visiva, non un secondo target. */}
          <span className="text-[13px] text-[hsl(210,64%,29%)] flex items-center gap-1">
            Dettagli ordine <ChevronRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────── */
export default function OrdersPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const search = useDebounce(searchInput, 300);

  const { data, loading, error, reload } = useMyOrdini({
    page: currentPage,
    pageSize: 25,
    stato: statusFilter || undefined,
    search: search || undefined,
  });

  const resetPage = () => setCurrentPage(1);

  // "Stampa elenco": legge tutte le pagine dei filtri correnti, poi stampa.
  const [stampa, setStampa] = useState<MyOrdine[] | null>(null);
  const [preparandoStampa, setPreparandoStampa] = useState(false);
  const stampaElenco = async () => {
    setPreparandoStampa(true);
    try {
      const tutti: MyOrdine[] = [];
      for (let page = 1; page <= 50; page++) {
        const qs = new URLSearchParams({ page: String(page), page_size: '200' });
        if (statusFilter) qs.set('stato', statusFilter);
        if (search) qs.set('search', search);
        const { data: r } = await api.get<MyOrdiniResp>(`/api/clienti/me/ordini?${qs}`);
        tutti.push(...r.items);
        if (page >= (r.pages ?? 1)) break;
      }
      setStampa(tutti);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? "Non è stato possibile preparare la stampa: riprova.");
    } finally {
      setPreparandoStampa(false);
    }
  };
  useEffect(() => {
    if (!stampa) return;
    const fine = () => setStampa(null);
    window.addEventListener('afterprint', fine);
    const t = setTimeout(() => window.print(), 100);
    return () => { clearTimeout(t); window.removeEventListener('afterprint', fine); };
  }, [stampa]);
  const filtriStampa = [statusFilter && `stato: ${statoOrdine(statusFilter).label}`, search && `ricerca: "${search}"`]
    .filter(Boolean).join(' · ');

  const total      = data?.total ?? 0;
  const totalPages = data?.pages ?? 1;
  const aggiornati = data?.aggiornati ?? 0;
  const [azzerando, setAzzerando] = useState(false);

  const segnaTuttiVisti = async () => {
    setAzzerando(true);
    try {
      await api.post('/api/clienti/me/ordini/visti');
      aggiornaContatori();
      reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'Non è stato possibile aggiornare: riprova.');
    } finally {
      setAzzerando(false);
    }
  };

  return (
    <div data-testid="orders-page" className="space-y-5">
      {stampa && <ReportOrdini righe={stampa} filtri={filtriStampa} />}
      <div className={`space-y-5 ${stampa ? 'print:hidden' : ''}`}>

      {/* ── Titolo ── */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Package className="w-6 h-6 text-[hsl(210,64%,29%)]" />
          I miei ordini
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {total > 0
            ? <>{total} ordini totali · live dal gestionale NTS</>
            : <>Tutti gli ordini emessi, dati dal gestionale NTS</>}
        </p>
        </div>
        {total > 0 && (
          <button type="button" onClick={stampaElenco} disabled={preparandoStampa}
            data-testid="orders-print"
            className="no-print h-9 px-3 rounded-lg border bg-white text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[hsl(210,40%,97%)] disabled:opacity-50">
            {preparandoStampa ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Stampa elenco
          </button>
        )}
      </div>

      {/* ── Novità non viste ── */}
      {aggiornati > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-rose-200 bg-rose-50/60 px-4 py-3"
          data-testid="orders-aggiornati">
          <p className="text-sm text-rose-900 flex-1 min-w-[12rem]">
            <span className="font-semibold">
              {aggiornati === 1 ? '1 ordine aggiornato' : `${aggiornati} ordini aggiornati`}
            </span>{' '}
            da quando li hai visti l'ultima volta: aprili per vedere le novità.
          </p>
          <button type="button" onClick={segnaTuttiVisti} disabled={azzerando}
            data-testid="orders-segna-tutti-visti"
            className="tap h-9 px-3 rounded-lg border border-rose-300 bg-white text-sm font-medium text-rose-800 hover:bg-rose-100 disabled:opacity-50 inline-flex items-center gap-1.5">
            {azzerando && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Segna tutti come visti
          </button>
        </div>
      )}

      {/* ── Toolbar ── */}
      {/* Su 390px ricerca e filtro affiancati riducevano il campo a ~230px
          e troncavano il placeholder: sotto sm si impilano a piena larghezza. */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2">
        {/* Ricerca */}
        <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={e => { setSearchInput(e.target.value); resetPage(); }}
            placeholder="Cerca N° ordine o SKU…"
            data-testid="order-search-input"
            className="tap h-11 md:h-9 w-full pl-9 pr-9 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30"
          />
          {searchInput && (
            <button onClick={() => { setSearchInput(''); resetPage(); }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Filtro stato */}
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); resetPage(); }}
          data-testid="order-status-filter"
          className="tap h-11 md:h-9 w-full sm:w-auto px-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30"
        >
          <option value="">Tutti gli stati</option>
          <option value="confermato">Confermati</option>
          <option value="evaso_parziale">Evasione parziale</option>
          <option value="sospeso">Sospesi</option>
          <option value="evaso">Evasi</option>
          <option value="annullato">Annullati</option>
        </select>

        {(searchInput || statusFilter) && (
          <button
            onClick={() => { setSearchInput(''); setStatusFilter(''); resetPage(); }}
            data-testid="order-filters-reset"
            className="tap h-11 md:h-9 w-full sm:w-auto px-3 rounded-lg border bg-white text-xs font-medium hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 transition-colors flex items-center justify-center gap-1">
            <X className="w-3 h-3" /> Reset
          </button>
        )}
      </div>

      {/* ── Errore ── */}
      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-center gap-2"
          data-testid="orders-error">
          <AlertCircle className="w-4 h-4" /> {error}
          <button onClick={reload} className="ml-auto underline text-xs">Riprova</button>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && !data && (
        <div className="rounded-xl border bg-white p-12 flex items-center justify-center text-muted-foreground"
          data-testid="orders-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento ordini…
        </div>
      )}

      {/* ── Empty ── */}
      {!loading && data?.items.length === 0 && (
        <div className="rounded-xl border bg-white p-12 text-center" data-testid="orders-empty">
          <Inbox className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <h3 className="text-base font-semibold">Nessun ordine trovato</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {search || statusFilter
              ? 'Nessun ordine corrisponde ai filtri impostati.'
              : 'Non hai ancora effettuato ordini. Inizia ad esplorare il catalogo!'}
          </p>
          {(search || statusFilter) && (
            <button onClick={() => { setSearchInput(''); setStatusFilter(''); resetPage(); }}
              className="mt-4 text-sm text-[hsl(210,64%,29%)] hover:underline">
              Rimuovi filtri
            </button>
          )}
        </div>
      )}

      {/* ── Tabella ordini ── */}
      {data && data.items.length > 0 && (
        <div className="rounded-xl border bg-white overflow-hidden shadow-sm">

          {/* Header colonne */}
          <div className="hidden md:grid grid-cols-[7.5rem_9rem_1fr_8rem_7rem_2.5rem] px-4 py-2.5 bg-[hsl(210,64%,29%)] text-white text-[11px] font-semibold uppercase tracking-wide">
            <div>Data</div>
            <div>N° Ordine</div>
            <div>Destinazione</div>
            <div>Stato</div>
            <div className="text-right">Totale IVA incl.</div>
            <div />
          </div>
          {/* Nessun header su mobile: con le card le intestazioni di
              colonna non hanno colonne da intestare. */}

          {/* Righe */}
          <div data-testid="orders-table">
            {data.items.map((ordine, idx) => (
              <OrderRow key={ordine.id} ordine={ordine} idx={idx} />
            ))}
          </div>

          {/* ── Paginazione ── */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t bg-[hsl(210,40%,98%)] flex items-center gap-2 justify-between"
              data-testid="orders-pagination">
              <p className="text-xs text-muted-foreground">
                {total} ordini · pagina{' '}
                <span className="font-semibold">{currentPage}</span>{' '}
                di <span className="font-semibold">{totalPages}</span>
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || loading}
                  data-testid="orders-pagination-prev"
                  className="h-7 w-7 rounded border text-xs flex items-center justify-center disabled:opacity-40 hover:bg-white transition-colors">
                  ‹
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                  return start + i;
                }).map(p => (
                  <button key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`h-7 w-7 rounded border text-xs transition-colors ${
                      p === currentPage
                        ? 'bg-[hsl(210,64%,29%)] text-white border-[hsl(210,64%,29%)]'
                        : 'hover:bg-white'
                    }`}>
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || loading}
                  data-testid="orders-pagination-next"
                  className="h-7 w-7 rounded border text-xs flex items-center justify-center disabled:opacity-40 hover:bg-white transition-colors">
                  ›
                </button>
              </div>
            </div>
          )}

          {/* Footer semplice per pagina unica */}
          {totalPages === 1 && total > 0 && (
            <div className="px-4 py-2.5 border-t bg-[hsl(210,40%,98%)] text-xs text-muted-foreground text-center">
              {total} {total === 1 ? 'ordine' : 'ordini'}
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
